/**
 * Notetaker settings section.
 *
 * Gated by the `recall_notetaker` feature flag. Two clearly separated controls:
 *  - "Your recording" — per-user toggle (everyday touch point, shown FIRST)
 *  - "Team setting"   — org-level admin enable/disable (rare, shown SECOND)
 *
 * Visibility:
 *  - Team disabled: personal section is shown but toggle is disabled. Team
 *    section shows either a plain Enable button (admins) or a read-only
 *    "contact your admin" line.
 *  - Team enabled: personal section is the primary content; team section shows
 *    a single-row status + Disable button (admins only).
 *
 * The personal choice is rendered as a binary Toggle. The backend enum keeps
 * its 4 values (`auto`/`bot_only`/`sdk_only`/`disabled`) so the desktop app
 * can still write `bot_only`/`sdk_only` — the UI just doesn't expose them.
 * Anything that isn't `disabled` reads as "recording on"; flipping ON always
 * writes `auto`.
 */

import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useOrg } from '@/contexts/OrgContext';
import {
  useRecallAdminStatus,
  useRecallAdminToggle,
} from '@/hooks/integrations/recall/useRecallAdminStatus';
import { useRecallPreference } from '@/hooks/integrations/recall/useRecallPreference';
import { useCanWrite } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { Toggle } from '@/lib/base/forms/toggle';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { NOTETAKER_SUPPORTED_PLATFORMS_SENTENCE } from '@/lib/crm/utils/notetakerPlatforms';
import { useViewHealthMarker } from '@/lib/view-health';

import { PersonalAdvancedSettings } from './personal-advanced-settings';

// ---------------------------------------------------------------------------
// Module-scope copy constants
// ---------------------------------------------------------------------------

const PAGE_DESCRIPTION =
  'Ahoy joins your meetings to capture a transcript, summary, and key action items.';

const RECORDING_ON_HINT =
  'Ahoy joins as a participant. If you have the Ahoy desktop app running, it records locally instead.';
const RECORDING_OFF_HINT =
  'No bot will join your meetings. If you have the Ahoy desktop app running, it may still record locally – quit the app to disable that as well.';

const DISCLOSURE_HELPER =
  'Other participants can see when the Ahoy bot joins. The desktop app records silently – you may need to disclose recording to participants.';

const TEAM_MEETING_NOTE =
  'When multiple team members are in the same meeting, only one bot joins. The transcript and summary are shared with your whole team – no duplicates.';

const SUPPORTED_PLATFORMS_NOTE = `The bot joins ${NOTETAKER_SUPPORTED_PLATFORMS_SENTENCE} calls. Meetings on other platforms are skipped – the Ahoy desktop app can record those locally.`;

// ---------------------------------------------------------------------------
// NotetakerSection — wires hooks to a pure render body
// ---------------------------------------------------------------------------

export function NotetakerSection() {
  useViewHealthMarker('settings-section-ready');

  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;

  const adminStatus = useRecallAdminStatus(orgId);
  const adminToggle = useRecallAdminToggle(orgId);

  const handleEnable = useCallback(() => {
    adminToggle.mutate({ enabled: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutate is stable per React Query; the wrapper object isn't
  }, [adminToggle.mutate]);

  const handleDisable = useCallback(() => {
    adminToggle.mutate({ enabled: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutate is stable per React Query; the wrapper object isn't
  }, [adminToggle.mutate]);

  const orgEnabled = adminStatus.data?.enabled === true;

  return (
    <NotetakerSectionBody
      orgId={orgId}
      adminLoading={orgId === undefined || adminStatus.isLoading}
      adminError={adminStatus.isError}
      adminEnabled={orgEnabled}
      canToggle={adminStatus.data?.can_toggle === true}
      togglePending={adminToggle.isPending}
      toggleError={adminToggle.isError}
      onEnable={handleEnable}
      onDisable={handleDisable}
    />
  );
}

// ---------------------------------------------------------------------------
// NotetakerSectionBody — pure render logic
// ---------------------------------------------------------------------------

interface NotetakerSectionBodyProps {
  orgId: number | undefined;
  adminLoading: boolean;
  adminError: boolean;
  adminEnabled: boolean;
  canToggle: boolean;
  togglePending: boolean;
  toggleError: boolean;
  onEnable: () => void;
  onDisable: () => void;
}

function NotetakerSectionBody({
  orgId,
  adminLoading,
  adminError,
  adminEnabled,
  canToggle,
  togglePending,
  toggleError,
  onEnable,
  onDisable,
}: NotetakerSectionBodyProps) {
  if (adminLoading) {
    return <p className='text-sm text-fg-tertiary'>Loading…</p>;
  }

  if (adminError) {
    return (
      <p role='alert' className='text-sm text-utility-error-600'>
        Unable to load Notetaker settings. Please try again later.
      </p>
    );
  }

  return (
    <div className='max-w-2xl space-y-8'>
      {/* Page header — title + 1-line description, nothing else */}
      <header className='flex items-center gap-4'>
        <img
          src='/ahoy-notetaker-avatar.png'
          alt='Ahoy Notetaker'
          className='h-12 w-12 rounded-lg'
        />
        <div>
          <h2 className='text-lg font-semibold text-fg-primary'>Notetaker</h2>
          <p className='mt-1 text-sm text-fg-tertiary'>{PAGE_DESCRIPTION}</p>
        </div>
      </header>

      {/* PRIMARY: personal toggle — this is what most users come here for */}
      <PersonalRecordingSection orgId={orgId} adminEnabled={adminEnabled} />

      {/* SECONDARY: team setting (small, secondary visual weight) */}
      <TeamSettingSection
        adminEnabled={adminEnabled}
        canToggle={canToggle}
        togglePending={togglePending}
        toggleError={toggleError}
        onEnable={onEnable}
        onDisable={onDisable}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionEyebrow — small uppercase label + right-aligned scope hint.
// Used to label both subsections without the heavier pill-chip styling.
// ---------------------------------------------------------------------------

interface SectionEyebrowProps {
  title: string;
  hint?: string;
}

function SectionEyebrow({ title, hint }: SectionEyebrowProps) {
  return (
    <div className='flex items-baseline justify-between gap-3'>
      <h3 className='text-xs font-semibold uppercase tracking-wider text-fg-tertiary'>
        {title}
      </h3>
      {hint && <span className='text-xs text-fg-tertiary'>{hint}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PersonalRecordingSection — single Toggle in a card, plus a contextual
// disclosure helper and advanced settings (only when recording is ON).
// Shows a disabled state when org admin has disabled notetaker.
// ---------------------------------------------------------------------------

interface PersonalRecordingSectionProps {
  orgId: number | undefined;
  adminEnabled: boolean;
}

function PersonalRecordingSection({
  orgId,
  adminEnabled,
}: PersonalRecordingSectionProps) {
  const { preference, loading, updating, error, available, setPreference } =
    useRecallPreference(orgId);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const canWrite = useCanWrite();

  const isRecordingOn =
    preference !== null && preference.method_preference !== 'disabled';
  const needsConsent =
    preference !== null && preference.consent_acknowledged_at === null;
  const isToggleDisabled = !canWrite || !adminEnabled || updating;

  // Avoid nested ternaries (no-nested-ternary ESLint rule)
  const titleText =
    adminEnabled && isRecordingOn
      ? 'Record my meetings'
      : "Don't record my meetings";

  let hintText: string;
  if (!adminEnabled) {
    hintText = 'Notetaker is disabled by your account admin.';
  } else if (isRecordingOn) {
    hintText = RECORDING_ON_HINT;
  } else {
    hintText = RECORDING_OFF_HINT;
  }

  const handleToggle = useCallback(
    async (value: boolean) => {
      if (!value) {
        try {
          await setPreference({ method_preference: 'disabled' });
          toast.success('Preference updated');
        } catch {
          toast.error("We couldn't update the preference. Try again.");
        }
        return;
      }
      if (needsConsent) {
        setIsConsentModalOpen(true);
        return;
      }
      try {
        await setPreference({ method_preference: 'auto' });
        toast.success('Preference updated');
      } catch {
        toast.error("We couldn't update the preference. Try again.");
      }
    },
    [needsConsent, setPreference]
  );

  const handleCloseConsent = useCallback(() => {
    if (updating) {
      return;
    }
    setIsConsentModalOpen(false);
  }, [updating]);

  const handleConfirmConsent = useCallback(async () => {
    const timestamp = new Date().toISOString();
    try {
      await setPreference({
        method_preference: 'auto',
        consent_acknowledged_at: timestamp,
      });
      setIsConsentModalOpen(false);
      toast.success('Preference updated');
    } catch {
      toast.error("We couldn't update the preference. Try again.");
    }
  }, [setPreference]);

  if (!available || loading) {
    return null;
  }

  return (
    <section className='space-y-2'>
      <SectionEyebrow title='Your recording' hint='Only you' />

      {error && (
        <p
          role='alert'
          className='text-sm text-[var(--color-text-error-primary)]'
        >
          {error}
        </p>
      )}

      {/* Label/hint live OUTSIDE Toggle's built-in label/hint slots because
          Toggle's container is `w-max` and its inner text slot has no
          `min-w-0`, so long hint copy overflows the card. Rendering them in a
          flex sibling with `flex-1 min-w-0` lets the text wrap correctly. */}
      <div className='rounded-lg border border-[var(--color-border-secondary)] p-4'>
        <div className='flex items-start justify-between gap-4'>
          <div className='min-w-0 flex-1'>
            <p className='text-sm font-medium text-fg-primary'>{titleText}</p>
            <p
              id='notetaker-toggle-hint'
              className='mt-0.5 text-sm text-fg-tertiary'
            >
              {hintText}
            </p>
          </div>
          <Toggle
            size='md'
            isSelected={adminEnabled && isRecordingOn}
            onChange={handleToggle}
            isDisabled={isToggleDisabled}
            aria-label={
              isRecordingOn ? 'Record my meetings' : "Don't record my meetings"
            }
            aria-describedby='notetaker-toggle-hint'
            className='shrink-0'
          />
        </div>
      </div>

      {adminEnabled && isRecordingOn && (
        <>
          <p className='text-xs text-fg-tertiary px-1'>
            {SUPPORTED_PLATFORMS_NOTE}
          </p>
          <p className='text-xs text-fg-tertiary px-1'>{DISCLOSURE_HELPER}</p>
          <p className='text-xs text-fg-tertiary px-1'>{TEAM_MEETING_NOTE}</p>
          {preference && (
            <PersonalAdvancedSettings
              preference={preference}
              setPreference={setPreference}
            />
          )}
        </>
      )}

      <PersonalConsentModal
        isOpen={isConsentModalOpen}
        updating={updating}
        onClose={handleCloseConsent}
        onConfirm={handleConfirmConsent}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// PersonalConsentModal — shown on first enable to surface the legal
// acknowledgement at the individual-user level.
// ---------------------------------------------------------------------------

interface PersonalConsentModalProps {
  isOpen: boolean;
  updating: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function PersonalConsentModal({
  isOpen,
  updating,
  onClose,
  onConfirm,
}: PersonalConsentModalProps) {
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={!updating}
    >
      <Modal className='max-w-lg'>
        <Dialog
          title='Enable Notetaker'
          description='Quick heads-up before we turn this on – clicking Accept & enable counts as your acknowledgement.'
          footer={
            <div className='flex justify-end gap-2'>
              <Button color='secondary' onClick={onClose} isDisabled={updating}>
                Cancel
              </Button>
              <Button onClick={onConfirm} isDisabled={updating}>
                Accept &amp; enable
              </Button>
            </div>
          }
        >
          <div className='space-y-3 text-sm text-fg-secondary'>
            <p>
              When enabled, Ahoy joins your team&apos;s meetings as a visible
              participant and records them. Participants can see when the bot
              joins, but they aren&apos;t separately notified or asked for
              consent.
            </p>
            <p>
              You&apos;re responsible for any consent or notice requirements in
              your jurisdiction. Two-party-consent regions (some US states, EU
              under GDPR) may require additional disclosure that Ahoy
              doesn&apos;t provide automatically.
            </p>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

// ---------------------------------------------------------------------------
// TeamSettingSection — branches into four mutually-exclusive states.
// ---------------------------------------------------------------------------

interface TeamSettingSectionProps {
  adminEnabled: boolean;
  canToggle: boolean;
  togglePending: boolean;
  toggleError: boolean;
  onEnable: () => void;
  onDisable: () => void;
}

function TeamSettingSection({
  adminEnabled,
  canToggle,
  togglePending,
  toggleError,
  onEnable,
  onDisable,
}: TeamSettingSectionProps) {
  const eyebrowHint = canToggle ? 'You can change this' : 'Account admins only';

  return (
    <section className='space-y-2'>
      <SectionEyebrow title='Team setting' hint={eyebrowHint} />

      <TeamSettingCard
        adminEnabled={adminEnabled}
        canToggle={canToggle}
        togglePending={togglePending}
        toggleError={toggleError}
        onEnable={onEnable}
        onDisable={onDisable}
      />
    </section>
  );
}

function TeamSettingCard(props: TeamSettingSectionProps) {
  const {
    adminEnabled,
    canToggle,
    togglePending,
    toggleError,
    onEnable,
    onDisable,
  } = props;

  // State 1: enabled + admin can toggle → status + Disable
  if (adminEnabled && canToggle) {
    return (
      <div className='rounded-lg border border-[var(--color-border-secondary)] p-4'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <p className='text-sm text-fg-primary'>
            Notetaker is enabled for everyone on your team.
          </p>
          <Button
            color='secondary'
            isDisabled={togglePending}
            onClick={onDisable}
          >
            Disable
          </Button>
        </div>
        {toggleError && (
          <p role='alert' className='mt-3 text-sm text-utility-error-600'>
            We couldn't update the Notetaker settings. Try again.
          </p>
        )}
      </div>
    );
  }

  // State 2: enabled + non-admin → read-only status
  if (adminEnabled && !canToggle) {
    return (
      <div className='rounded-lg border border-[var(--color-border-secondary)] p-4'>
        <p className='text-sm text-fg-primary'>
          Notetaker is enabled for everyone on your team.
        </p>
        <p className='mt-1 text-xs text-fg-tertiary'>
          Only account admins can change this setting.
        </p>
      </div>
    );
  }

  // State 3: disabled + non-admin → contact admin
  if (!adminEnabled && !canToggle) {
    return (
      <div className='rounded-lg border border-[var(--color-border-secondary)] p-4'>
        <p className='text-sm text-fg-primary'>
          Notetaker is not enabled for your team.
        </p>
        <p className='mt-1 text-xs text-fg-tertiary'>
          Contact your account admin to turn it on.
        </p>
      </div>
    );
  }

  // State 4: disabled + admin → plain Enable button (no consent modal)
  return (
    <div className='rounded-lg border border-[var(--color-border-secondary)] p-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-medium text-fg-primary'>
            Notetaker isn&apos;t enabled for your team yet.
          </p>
          <p className='mt-1 text-sm text-fg-tertiary'>
            Enable it to give everyone transcripts, summaries, and action items
            from their meetings.
          </p>
        </div>
        <Button isDisabled={togglePending} onClick={onEnable}>
          Enable
        </Button>
      </div>
      {toggleError && (
        <p role='alert' className='mt-3 text-sm text-utility-error-600'>
          We couldn't update the Notetaker settings. Try again.
        </p>
      )}
    </div>
  );
}
